import React from 'react';
import clsx from 'clsx';
import { ActionModal, Button, Dropdown } from '@insurancemenu/imenu-components-lib';
import { DropdownOptionType, OptionsAlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types/Dropdown';
import {
  Bookmark,
  ChevronDown,
  Delete,
  Description,
  DriveFile,
  FilePdf,
  Star,
} from '@insurancemenu/imenu-components-lib/dist/icons';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import { useRootStore } from 'store/rootStore';
import { observer } from 'mobx-react-lite';
import { EnrollmentPeriod, Proposal, RfqProposalStatus } from 'types/enrollmentService';
import { createStyles } from './styles';

interface Props {
  cartId: string;
  enrollmentPeriod: EnrollmentPeriod;
  proposal: Proposal;
  redirectToProposalPage?: () => void;
  pdfDownloadUrl?: string;
}

const MoreDropdown: React.FC<Props> = observer(
  ({
    cartId,
    enrollmentPeriod: { id: enrollmentPeriodId, hasConfirmedProposal },
    proposal,
    redirectToProposalPage,
    pdfDownloadUrl,
  }) => {
    const { groupProposalsTableStore, updateProposalModalStore } = useRootStore();
    const { name: proposalName, confirmed: proposalConfirmed, rateSegment } = proposal;
    const [showFailingModal, setShowFailingModal] = React.useState(false);

    const classes = createStyles();

    function buildOptions(): DropdownOptionType[] {
      const ViewProposalOption: DropdownOptionType = {
        value: 'viewProposal',
        label: 'View Proposal',
        icon: DriveFile,
        disabled: !redirectToProposalPage,
        onOptionClick: (): void => {
          redirectToProposalPage?.();
        },
      };

      const RenameProposalOption: DropdownOptionType = {
        value: 'renameProposal',
        label: 'Rename Proposal',
        icon: Description,
        onOptionClick: (): void => {
          updateProposalModalStore.openRenameProposalModal(proposalName, cartId, enrollmentPeriodId);
        },
      };
      const MarkAsFavoriteOption: DropdownOptionType = {
        value: 'markAsFavorite',
        label: 'Mark as Favorite',
        icon: Star,
        onOptionClick: (): void => {
          updateProposalModalStore.openSaveProposalModal(proposalName, cartId, rateSegment);
        },
      };
      const MarkAsPreferredOption: DropdownOptionType = {
        value: 'markAsPreferred',
        label: 'Propose Quote',
        icon: Bookmark,
        onOptionClick: (): void => {
          groupProposalsTableStore.openConfirmationModal('MARK_AS_PREFERRED', { enrollmentPeriodId, cartId });
        },
      };
      const DeleteProposalOption: DropdownOptionType = {
        value: 'deleteProposal',
        label: 'Delete Proposal',
        icon: Delete,
        customColor: 'red',
        onOptionClick: (): void => {
          groupProposalsTableStore.openConfirmationModal('DELETE_PROPOSAL', { enrollmentPeriodId, cartId });
        },
      };

      const DownloadPdfOption: DropdownOptionType = {
        value: 'downloadProposalPdf',
        label: 'Download Proposal',
        icon: FilePdf,
        onOptionClick: (): void => {
          if (pdfDownloadUrl) {
            downloadByUrl(pdfDownloadUrl);
          }
        },
      };

      const options: DropdownOptionType[] = [ViewProposalOption];

      if (pdfDownloadUrl && proposal.rateSegment !== 'CUSTOM') {
        options.push(DownloadPdfOption);
      }

      if (proposal?.status && proposal.status !== RfqProposalStatus.RELEASED) {
        options.push(DeleteProposalOption);
        return options;
      }

      options.push(RenameProposalOption, MarkAsFavoriteOption);
      if (!hasConfirmedProposal) options.push(MarkAsPreferredOption);
      if (!proposalConfirmed) {
        options.push(DeleteProposalOption);
      }
      return options;
    }

    return (
      <>
        <Dropdown
          name="More"
          options={buildOptions()}
          optionsAlignment={OptionsAlignmentTypes.BOTTOM_LEFT}
          optionsWidth="22rem"
          closedElement={
            <Button
              className={clsx(classes.dropdownOpenedBtn, classes.dropdownButton)}
              color="primary"
              rightIconProp={<ChevronDown />}
            >
              More
            </Button>
          }
          openedElement={
            <Button color="primary" className={classes.dropdownButton} rightIconProp={<ChevronDown />}>
              More
            </Button>
          }
        />
        <ActionModal
          show={showFailingModal}
          onClose={(): void => {
            setShowFailingModal(false);
          }}
          title="Something went wrong during generating the proposal, please try again"
          subtitle={
            <p>
              If you still have issues, please send an email to <br />
              <a href="mailto:support@insurancemenu.com">support@insurancemenu.com</a>
            </p>
          }
        />
      </>
    );
  },
);

export default MoreDropdown;
